// Schedule in Blueprint (pages/ux-full screens/schedule.js): the words and figures the day board draws
// around the lanes. The board's own model (board-model.ts) stays the one for what a visit is and does;
// these only say it the way the prototype does.
import type { BoardVisit } from '../board-model.ts';
import { visitWord as sharedVisitWord } from '@flowology/ui-web/words';

/** How many pens Blueprint has (tokens: --bp-pen-1 … --bp-pen-8). */
export const PENS = 8;

/**
 * A technician's pen: Blueprint tells technicians apart by their pen, never by a state (DESIGN.md §2).
 * By their place in the board's order, so the same people keep the same pens day to day; a ninth
 * technician starts the pens again.
 */
export const penOf = (index: number): number => (((index % PENS) + PENS) % PENS) + 1;

/** "45 min", "1 h", "2 h 30". */
export function duration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60}` : ''}`;
}

/** The line under a technician's name: "3 visits, 5 h", or "Nothing booked". */
export function laneSummary(visits: readonly Pick<BoardVisit, 'start' | 'end'>[]): string {
  if (!visits.length) return 'Nothing booked';
  const minutes = visits.reduce((s, v) => s + (Date.parse(v.end) - Date.parse(v.start)) / 60_000, 0);
  return `${visits.length} ${visits.length === 1 ? 'visit' : 'visits'}, ${duration(minutes)}`;
}

export interface HourMark {
  /** Where along the day, 0 to 1. */
  at: number;
  label: string;
}

/**
 * The hours over the lanes, as the sample writes them: the first with its "a.m.", noon and one o'clock
 * named, the rest bare ("8 a.m.", "9", "12 p.m.", "1 p.m.", "2"). An hour within 35 minutes of now
 * gives way to the now line's own label.
 */
export function hourMarks(startHour: number, endHour: number, nowHour: number | null): HourMark[] {
  const span = endHour - startHour;
  const out: HourMark[] = [];
  for (let h = startHour; h <= endHour; h++) {
    if (nowHour !== null && Math.abs(h - nowHour) < 35 / 60) continue;
    const h12 = ((h + 11) % 12) + 1;
    const label =
      h === 12 ? '12 p.m.' : h === startHour || h === 13 ? `${h12} ${h < 12 ? 'a.m.' : 'p.m.'}` : String(h12);
    out.push({ at: (h - startHour) / span, label });
  }
  return out;
}

const dayNumber = (date: string) => Date.parse(`${date}T12:00:00Z`) / 86_400_000;

/** The board's title: "Today's board", "Tomorrow's board", "Yesterday's board", else "Friday's board". */
export function boardTitle(date: string, today: string): string {
  const diff = Math.round(dayNumber(date) - dayNumber(today));
  if (diff === 0) return 'Today’s board';
  if (diff === 1) return 'Tomorrow’s board';
  if (diff === -1) return 'Yesterday’s board';
  const day = new Intl.DateTimeFormat('en-CA', { weekday: 'long', timeZone: 'UTC' }).format(
    new Date(`${date}T12:00:00Z`),
  );
  return `${day}’s board`;
}

export interface BoardFigures {
  visits: number;
  done: number;
  working: number;
  late: number;
  waiting: number;
}

/**
 * The five figures over the board (the sample's): every visit on the day, those done, those on site
 * now, those running late or long (a ServiceTitan visit only as of its last read isn't counted), and
 * those nobody has yet.
 */
export function boardFigures(
  day: readonly Pick<BoardVisit, 'status'>[],
  late: number,
  waiting: number,
): BoardFigures {
  return {
    visits: day.length,
    done: day.filter((v) => v.status === 'closed').length,
    working: day.filter((v) => v.status === 'working').length,
    late,
    waiting,
  };
}

const SAID = new Set(['dispatched', 'working', 'paused', 'closed']);

/** The word a visit's third line gives for its state, where the drawing alone doesn't say it. */
export function visitWord(status: string, notStarted = false): string | null {
  // the app's one word for a visit (job-words); a booked one, or one called off, the drawing says
  // alone. Sent with nobody set off yet reads "Sent, not started" (QA-687), as the board's label does.
  return SAID.has(status) ? sharedVisitWord(status, { notStarted }) : null;
}
